import { formatMoney } from 'src/utils/utility';
import {
  CourierInvoiceDisputeDto,
  CourierShipmentDeviationsResolutionsDto,
  InvoiceDisputeDto,
  InvoiceSummaryDto,
  ShipmentInActionDetailDto,
} from 'src/generated/services/TFinancialApi';
import {
  IDisputeTableItem,
  OneOffOrRecurring,
  IDisputeFormValues,
  IDisputeCourierFormValues,
} from 'src/presentations/components/organisms/actionDetails/dispute/types';

import { DeviationWorkflowAction } from 'src/infra/types/deviation';

export const invoiceIssueCategories = {
  accessorialMissingApprovals: 'Accessorial charges missing approvals',
  serviceTypeMissingApprovals: 'Shipments missing approvals for special service',
  deviations: 'Total number of charge deviations',
  rejectedNRCFShipments: 'Shipments rejected (no rate card)',
};

export const invoiceDetailToDisputeTableItems = (shipment: ShipmentInActionDetailDto, invoiceDispute: InvoiceDisputeDto) => {
  if (!shipment || !shipment?.deviations) {
    return [];
  }

  return shipment.deviations?.map((item, index): IDisputeTableItem => {
    const deviationResolution = invoiceDispute?.deviationResolutions?.find((resolution) => resolution.deviationId === item.id);
    return {
      id: index + 1,
      deviationId: item.id,
      chargeName: `${item?.chargeDescription} / ${item?.name}`,
      deviationAmount: formatMoney(item?.deviationAmount, item?.legCurrency),
      expectedAmount: deviationResolution?.expectedAmount ?? item?.deviationAmount,
      validityPeriod:
        deviationResolution?.periodType === 'RECURRING'
          ? { start: new Date(deviationResolution?.startPeriod), end: new Date(deviationResolution?.endPeriod) }
          : null,
      currencyType: item.deviationCurrency,
      oneOffOrRecurring: deviationResolution?.periodType ?? OneOffOrRecurring.OneOff,
      status: item?.status,
    };
  });
};

export const courierinvoiceDetailToDisputeTableItems = (data: CourierShipmentDeviationsResolutionsDto) =>
  data.deviations.map((item, index): IDisputeTableItem => {
    const deviationResolution = data.deviationResolution?.find((resolution) => resolution.deviationId === item.id);
    return {
      id: index + 1,
      deviationId: item.id,
      chargeName: `${item?.chargeDescription} / ${item?.name}`,
      deviationAmount: formatMoney(item?.deviationAmount, item?.legCurrency),
      expectedAmount: deviationResolution?.expectedAmount ?? item?.deviationAmount,
      validityPeriod:
        deviationResolution?.periodType === 'RECURRING'
          ? { start: new Date(deviationResolution?.startPeriod), end: new Date(deviationResolution?.endPeriod) }
          : null,
      currencyType: item.deviationCurrency,
      oneOffOrRecurring: deviationResolution?.periodType ?? OneOffOrRecurring.OneOff,
      status: item?.status,
    };
  });

export const invoiceAndDisputeDetailsToDisputeFormValues = (data: InvoiceSummaryDto): IDisputeFormValues => {
  const invoiceDisputeDetail = data?.invoiceDispute;
  const deviationItems = [];

  // Determine if this is a draft (user has previously interacted with the form)
  const isDraft = invoiceDisputeDetail?.resolutionDescription && invoiceDisputeDetail?.resolutionDescription !== '';

  // Backend agreementResolution is null for both "no selection" and "cancel dispute"
  // Use isDraft to distinguish:
  // - If isDraft=false and agreementResolution=null -> no selection made yet (null)
  // - If isDraft=true and agreementResolution=null -> user chose cancel dispute ('CANCEL_DISPUTE')
  let agreementType: IDisputeFormValues['agreementType'] = invoiceDisputeDetail?.agreementResolution ?? null;

  if (agreementType === null && isDraft) {
    // User has a draft with null agreementResolution = they chose cancel dispute
    agreementType = 'CANCEL_DISPUTE';
  }

  // Determine amountOption based on lumpSumAmount
  let amountOption: 'FULL_INVOICE' | 'DEVIATIONS' | 'OTHER' | undefined = undefined;
  let otherAmount = '';

  if (agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' && invoiceDisputeDetail?.lumpSumAmount) {
    const lumpSum = invoiceDisputeDetail.lumpSumAmount;
    const fullAmount = data?.invoiceAmount?.afterTax;
    const deviationsAmount = data?.invoiceAmountDeviation?.afterTax;

    // Check if lumpSumAmount matches full invoice or deviations
    if (fullAmount !== undefined && Math.abs(lumpSum - fullAmount) < 0.01) {
      amountOption = 'FULL_INVOICE';
    } else if (deviationsAmount !== undefined && Math.abs(lumpSum - deviationsAmount) < 0.01) {
      amountOption = 'DEVIATIONS';
    } else {
      amountOption = 'OTHER';
      otherAmount = lumpSum.toString();
    }
  }

  return {
    agreementType,
    reason: invoiceDisputeDetail?.resolutionDescription ?? '',
    expectedAmount: invoiceDisputeDetail?.deviationResolutionType ?? null,
    disputeTableItems: deviationItems,
    amountOption,
    otherAmount,
    totalAmount: '', // This is calculated at submission time
  };
};

export const courierInvoiceAndDisputeDetailsToDisputeFormValues = (data: CourierInvoiceDisputeDto): IDisputeCourierFormValues => {
  const deviationItems = data?.shipmentDeviations.flatMap((d) => courierinvoiceDetailToDisputeTableItems(d));

  return {
    agreementType: data?.agreementResolution ?? null,
    reason: data?.resolutionDescription ?? '',
    expectedAmount: data?.deviationResolutionType ?? null,
    disputeTableItems: deviationItems,
    totalAmount: data?.lumpSumAmount?.toString() ?? '',
  };
};
